'use client'
import Cookies from 'js-cookie'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

// 同意结果持久化在 cookie 中（而非 localStorage），便于服务端在需要时读取，
// 且符合「同意凭证本身属于必要 cookie」的合规惯例。
export const COOKIE_CONSENT_KEY = 'cookie-consent'

// 同意有效期（天）。到期后视为未决定，重新征求同意。
const CONSENT_EXPIRES = 180

// 同意条款版本号：当新增/调整受同意约束的 cookie 用途时递增，
// 旧版本的同意会通过 persist 的 migrate 失效，从而再次弹出横幅。
export const CONSENT_VERSION = 1

export type ConsentCategory = 'necessary' | 'functional' | 'analytics'

// 可由用户开关的分类（necessary 始终为 true，不参与开关）
export type OptionalConsent = {
  functional: boolean
  analytics: boolean
}

type CookieConsentState = OptionalConsent & {
  // 严格必要：登录鉴权、安全、核心功能，始终开启
  necessary: true
  // 是否已经做过选择（用于决定是否展示横幅）
  decided: boolean
  // 偏好设置弹窗是否打开（瞬时 UI 状态，不持久化）
  settingsOpen: boolean

  acceptAll: () => void
  rejectAll: () => void
  save: (prefs: Partial<OptionalConsent>) => void
  openSettings: () => void
  closeSettings: () => void
}

// cookie 存储适配器：服务端（无 document）时降级为空操作，避免 SSR 报错。
const cookieStorage = {
  getItem: (name: string) => {
    if (typeof document === 'undefined') return null
    return Cookies.get(name) ?? null
  },
  setItem: (name: string, value: string) => {
    if (typeof document === 'undefined') return
    Cookies.set(name, value, {
      expires: CONSENT_EXPIRES,
      sameSite: 'lax',
      path: '/',
      secure: typeof location !== 'undefined' && location.protocol === 'https:',
    })
  },
  removeItem: (name: string) => {
    if (typeof document === 'undefined') return
    Cookies.remove(name, { path: '/' })
  },
}

export const useCookieConsentStore = create<CookieConsentState>()(
  persist(
    (set) => ({
      necessary: true,
      // 合规策略为「选择退出」（opt-out）：功能性与分析类 cookie 默认开启，
      // 用户可在横幅或偏好设置中主动关闭。
      functional: true,
      analytics: true,
      decided: false,
      settingsOpen: false,

      acceptAll: () => set({ functional: true, analytics: true, decided: true, settingsOpen: false }),
      rejectAll: () => set({ functional: false, analytics: false, decided: true, settingsOpen: false }),
      save: (prefs) => set({ ...prefs, decided: true, settingsOpen: false }),
      openSettings: () => set({ settingsOpen: true }),
      closeSettings: () => set({ settingsOpen: false }),
    }),
    {
      name: COOKIE_CONSENT_KEY,
      storage: createJSONStorage(() => cookieStorage),
      version: CONSENT_VERSION,
      // 仅持久化同意结果，瞬时 UI 状态与方法不写入 cookie
      partialize: (state) => ({
        necessary: state.necessary,
        functional: state.functional,
        analytics: state.analytics,
        decided: state.decided,
      }),
      // 版本不一致（条款更新）时重置为未决定并重新展示横幅；
      // 选择退出策略下，重新征求期间各分类仍默认开启。
      migrate: () => ({
        necessary: true as const,
        functional: true,
        analytics: true,
        decided: false,
      }),
    },
  ),
)

// —— 供 React 之外（如分析 SDK 初始化）读取与订阅同意状态 —— //

// 同步读取某一分类是否已获得同意
export const hasConsent = (category: ConsentCategory): boolean => {
  if (category === 'necessary') return true
  return !!useCookieConsentStore.getState()[category]
}

// 订阅同意变化，返回取消订阅函数
export const subscribeConsent = (
  listener: (state: CookieConsentState) => void,
) => useCookieConsentStore.subscribe(listener)

// —— 地区判断：仅在法律要求事先告知/同意的地区展示横幅 —— //
// 覆盖欧盟成员国 + EEA（冰岛/列支敦士登/挪威）+ 英国 + 瑞士的 IANA 时区。
// 用时区（而非 IP）判断：纯前端、即时、无需后端，适合作为横幅展示的门槛。
const CONSENT_REQUIRED_TIMEZONES = new Set<string>([
  // 奥地利、比利时、保加利亚、克罗地亚、塞浦路斯、捷克
  'Europe/Vienna', 'Europe/Brussels', 'Europe/Sofia', 'Europe/Zagreb', 'Asia/Nicosia', 'Europe/Nicosia', 'Europe/Prague',
  // 丹麦、爱沙尼亚、芬兰、法国、德国、希腊、匈牙利
  'Europe/Copenhagen', 'Europe/Tallinn', 'Europe/Helsinki', 'Europe/Paris', 'Europe/Berlin', 'Europe/Busingen', 'Europe/Athens', 'Europe/Budapest',
  // 爱尔兰、意大利、拉脱维亚、立陶宛、卢森堡、马耳他、荷兰
  'Europe/Dublin', 'Europe/Rome', 'Europe/Riga', 'Europe/Vilnius', 'Europe/Luxembourg', 'Europe/Malta', 'Europe/Amsterdam',
  // 波兰、葡萄牙、罗马尼亚、斯洛伐克、斯洛文尼亚
  'Europe/Warsaw', 'Europe/Lisbon', 'Atlantic/Azores', 'Atlantic/Madeira', 'Europe/Bucharest', 'Europe/Bratislava', 'Europe/Ljubljana',
  // 西班牙、瑞典
  'Europe/Madrid', 'Africa/Ceuta', 'Atlantic/Canary', 'Europe/Stockholm',
  // EEA：冰岛、列支敦士登、挪威
  'Atlantic/Reykjavik', 'Europe/Vaduz', 'Europe/Oslo',
  // 英国 + 皇家属地
  'Europe/London', 'Europe/Belfast', 'Europe/Guernsey', 'Europe/Isle_of_Man', 'Europe/Jersey',
  // 瑞士
  'Europe/Zurich',
])

// 当前访问者是否处于需要同意的地区。无法判断时默认 false（不打扰）。
export const isConsentRequiredRegion = (): boolean => {
  if (typeof Intl === 'undefined') return false
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    return !!tz && CONSENT_REQUIRED_TIMEZONES.has(tz)
  } catch {
    return false
  }
}
